import { ACCEPT_IMAGE_MIMETYPE } from '@/app/constants';
import { createImage } from '../image-utils';
import { compressImageBlob } from './image-opts';

/**
 * `compressImageIfNeeded` 的限制：与工具侧 `useUploadFile` 历史口径一致，
 * **仅**用于本函数内部的"是否压缩"判定，不参与上层 50MB/128×128/4:1 校验。
 */
const DEFAULT_MAX_UPLOAD_SIZE = 10 * 1024 * 1024; // 10MB
const DEFAULT_MAX_AREA = 6000 * 6000; // 数美审核的图片像素限制

export type ImagePrepareFailureReason = 'compressFailed';

export interface ImagePrepareSuccess<TFile extends Blob = File> {
  ok: true;
  file: TFile;
  /** 最终（压缩后或原始）图片宽度。非图片输入回传入参 `width` 或 0。 */
  width: number;
  /** 最终（压缩后或原始）图片高度。非图片输入回传入参 `height` 或 0。 */
  height: number;
  /** 是否真正发生了压缩 / resize。 */
  compressed: boolean;
}

export interface ImagePrepareFailure<TFile extends Blob = File> {
  ok: false;
  reason: ImagePrepareFailureReason;
  /** 命中"文件大小超限"判定。 */
  exceedsSize: boolean;
  /** 命中"图片面积超限"判定。 */
  exceedsArea: boolean;
  /** 失败兜底返回的原始输入，方便上层做诊断 / 展示。 */
  originalFile: TFile;
}

export type ImagePrepareResult<TFile extends Blob = File> = ImagePrepareSuccess<TFile> | ImagePrepareFailure<TFile>;

export interface CompressImageIfNeededOptions {
  /**
   * 是否需要压缩的"文件大小"阈值（字节）。默认 10MB。超过该值即触发压缩。
   */
  maxUploadSize?: number;
  /**
   * 是否需要压缩的"图片面积"阈值（宽 × 高）。默认 6000 × 6000。
   * 超过该值会先 resize 到此面积之内，再走 webp 转换。
   */
  maxArea?: number;
  /** 已知图片宽度，避免重复 decode。 */
  width?: number;
  /** 已知图片高度，避免重复 decode。 */
  height?: number;
  /**
   * 视为图片的 MIME 白名单。默认与全局 `ACCEPT_IMAGE_MIMETYPE` 对齐。
   * 不在白名单的输入直接 passthrough，不做任何处理。
   */
  imageMimes?: readonly string[];
}

const splitFileNameAndExt = (name: string): { base: string; ext: string } => {
  const lastDot = name.lastIndexOf('.');
  if (lastDot <= 0) {
    return { base: name, ext: '' };
  }
  return { base: name.slice(0, lastDot), ext: name.slice(lastDot + 1).toLowerCase() };
};

const renameForCompressedBlob = (originalName: string, blob: Blob): string => {
  const { base } = splitFileNameAndExt(originalName);
  if (blob.type === 'image/webp') return `${base}.webp`;
  if (blob.type === 'image/jpeg') return `${base}.jpg`;
  if (blob.type === 'image/png') return `${base}.png`;
  return originalName;
};

const passthrough = <TInput extends Blob>(
  file: TInput,
  width: number,
  height: number,
): ImagePrepareSuccess<TInput> => ({
  ok: true,
  file,
  width,
  height,
  compressed: false,
});

const readImageDimensions = async (blob: Blob): Promise<{ width: number; height: number }> => {
  const imageUrl = URL.createObjectURL(blob);
  try {
    const image = await createImage(imageUrl);
    return { width: image?.width ?? 0, height: image?.height ?? 0 };
  } finally {
    URL.revokeObjectURL(imageUrl);
  }
};

/**
 * 在上传之前对图片做"按需压缩"，与工具侧 `useUploadFile` 行为对齐：
 *
 * 1. 非图片输入直接 passthrough，返回 `compressed: false`；
 * 2. 图片输入读尺寸（若调用方未提供 `width/height`），任一命中
 *    `size > maxUploadSize` 或 `width * height > maxArea` 即触发
 *    {@link compressImageBlob}（resize 到限制面积内 → 转 webp，
 *    必要时兜底 resize 到 4K 面积、降质转 jpeg）；
 * 3. 输入是 `File` 时，压缩后的 Blob 会重新包成 `File`（保留语义化
 *    `name` + 与压缩格式匹配的后缀），方便上层 OSS 直传场景里继续用
 *    `file.name`；输入是 `Blob`（工具侧 useUploadFile 路径）时直接回传
 *    Blob，由上层自行决定如何附 metadata；
 * 4. 压缩失败回报 `reason: 'compressFailed'` + `exceedsSize / exceedsArea`，
 *    上层据此做 toast / Sentry 上报。
 *
 * 设计要点：纯函数，不依赖 React / next-intl，可在工具侧上传链路与 claw
 * 上传链路里复用，是"图片上传按需压缩"的唯一实现入口。
 */
export async function compressImageIfNeeded(
  file: File,
  options?: CompressImageIfNeededOptions,
): Promise<ImagePrepareResult<File>>;
export async function compressImageIfNeeded(
  file: Blob,
  options?: CompressImageIfNeededOptions,
): Promise<ImagePrepareResult<Blob>>;
export async function compressImageIfNeeded(
  file: Blob,
  options: CompressImageIfNeededOptions = {},
): Promise<ImagePrepareResult<Blob>> {
  const maxUploadSize = options.maxUploadSize ?? DEFAULT_MAX_UPLOAD_SIZE;
  const maxArea = options.maxArea ?? DEFAULT_MAX_AREA;
  const imageMimes = options.imageMimes ?? ACCEPT_IMAGE_MIMETYPE;

  if (!imageMimes.includes(file.type)) {
    return passthrough(file, options.width ?? 0, options.height ?? 0);
  }

  let width = options.width ?? 0;
  let height = options.height ?? 0;
  if (width === 0 || height === 0) {
    try {
      const dim = await readImageDimensions(file);
      width = dim.width;
      height = dim.height;
    } catch {
      // 读取尺寸失败：保守返回 width/height = 0，由后续 area 判定决定走向。
    }
  }

  const exceedsSize = file.size > maxUploadSize;
  const exceedsArea = width * height > maxArea;

  if (!exceedsSize && !exceedsArea) {
    return passthrough(file, width, height);
  }

  const compressed = await compressImageBlob(file, maxUploadSize, maxArea);
  if (!compressed) {
    return {
      ok: false,
      reason: 'compressFailed',
      exceedsSize,
      exceedsArea,
      originalFile: file,
    };
  }

  // File 输入 → 重新包成 File 保留 name 语义；Blob 输入 → 直接回 Blob
  const compressedFile: Blob =
    file instanceof File
      ? new File([compressed.blob], renameForCompressedBlob(file.name, compressed.blob), {
          type: compressed.blob.type,
          lastModified: file.lastModified,
        })
      : compressed.blob;

  return {
    ok: true,
    file: compressedFile,
    width: compressed.width,
    height: compressed.height,
    compressed: true,
  };
}
